'use client';

/**
 * "Agent Tools & Thoughts" — platform-admin-only debug-feed inspector.
 *
 * A moveable / minimizable / resizable, tabbed dialog. Tab 0 = "Available
 * Feeds" (the feed catalog ∩ feeds actually emitting for this call). Click a
 * feed → opens a tab rendering that feed's renderer. Mounted once on the call
 * page; opened from the user menu (admins only) or Ctrl/Cmd+Shift+A.
 *
 * See docs/AGENT_DEBUG_FEEDS_DESIGN.md §5.3.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bug, X, Minus, Square, Circle, Trash2, Download, Pause, Play, Clock } from 'lucide-react';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { useAgentDebug } from '@/contexts/AgentDebugContext';
import { useAgentDebugStream, type AgentDebugConnectionState } from '@/hooks/useAgentDebugStream';
import { resolveDescriptor } from './feedRegistry';
import { TurnTimelineRenderer } from './renderers/TurnTimelineRenderer';

// Synthetic always-present "feed": a cross-feed view grouped by turnId.
const TIMELINE_FEED_ID = '__timeline__';
function tabLabel(id: string): string {
  if (id === TIMELINE_FEED_ID) return 'Turn Timeline';
  const [source, feed] = id.split('/');
  return resolveDescriptor(source, feed).label;
}

const LS_KEY = 'agentDebugDialog:v1';
const MIN_W = 480;
const MIN_H = 320;
const DEFAULT_W = 960;
const DEFAULT_H = 640;

interface PersistedLayout {
  x: number;
  y: number;
  w: number;
  h: number;
  minimized: boolean;
  openFeedIds: string[];
}

function loadLayout(): PersistedLayout {
  if (typeof window === 'undefined') return { x: 80, y: 80, w: DEFAULT_W, h: DEFAULT_H, minimized: false, openFeedIds: [] };
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<PersistedLayout>;
      return {
        x: typeof p.x === 'number' ? p.x : 80,
        y: typeof p.y === 'number' ? p.y : 80,
        w: Math.max(MIN_W, typeof p.w === 'number' ? p.w : DEFAULT_W),
        h: Math.max(MIN_H, typeof p.h === 'number' ? p.h : DEFAULT_H),
        minimized: !!p.minimized,
        openFeedIds: Array.isArray(p.openFeedIds) ? p.openFeedIds.filter((x) => typeof x === 'string') : [],
      };
    }
  } catch {
    /* ignore */
  }
  return { x: 80, y: 80, w: DEFAULT_W, h: DEFAULT_H, minimized: false, openFeedIds: [] };
}

const CONN_DOT: Record<AgentDebugConnectionState, string> = {
  idle: 'text-slate-500',
  connecting: 'text-amber-400 animate-pulse',
  open: 'text-emerald-400',
  closed: 'text-slate-500',
  error: 'text-red-400',
};

export function AgentDebugDialog() {
  const { isPlatformAdmin } = useIsAdmin();
  const { isOpen, close, toggle, callId } = useAgentDebug();

  // ── Ctrl/Cmd+Shift+A toggle (admins only) ────────────────────────────────
  useEffect(() => {
    if (!isPlatformAdmin) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isPlatformAdmin, toggle]);

  if (!isOpen || !isPlatformAdmin) return null;
  return <DialogInner callId={callId} onClose={close} />;
}

function DialogInner({ callId, onClose }: { callId: string | null; onClose: () => void }) {
  // Accessible name wiring — same accessible-name wiring the removed AI Home drawer used:
  // React.useId() -> id on the rendered title -> aria-labelledby on the dialog.
  // The title is the one already rendered in the title bar / minimized pill; no
  // invented label. (#1141 defect 4)
  const titleId = React.useId();
  const [layout, setLayout] = useState<PersistedLayout>(loadLayout);
  const [activeTab, setActiveTab] = useState<string>('feeds'); // 'feeds' | feedId
  const [pausedAll, setPausedAll] = useState(false);

  // ── Focus management (#1141) ─────────────────────────────────────────────
  // Runs for BOTH the minimized-pill and full-dialog renders (declared before
  // the minimized early-return so the hook order stays stable). This is a
  // NON-modal, movable tool window — we deliberately do NOT trap focus, because
  // an operator must stay able to interact with the live call behind it. We
  // move focus into the dialog on open and return it to the opener on close for
  // screen readers. Escape-to-close lives in ONE place — the "Escape closes"
  // effect below — a second listener here closed the dialog twice per keypress.
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    previouslyFocusedRef.current = (document.activeElement as HTMLElement | null) ?? null;
    const raf = requestAnimationFrame(() => dialogRef.current?.focus());
    return () => {
      cancelAnimationFrame(raf);
      const prev = previouslyFocusedRef.current;
      if (prev && typeof prev.focus === 'function' && document.contains(prev)) {
        prev.focus();
      }
    };
  }, []);

  // Persist layout (debounced via rAF-ish).
  useEffect(() => {
    try {
      window.localStorage.setItem(LS_KEY, JSON.stringify(layout));
    } catch {
      /* ignore */
    }
  }, [layout]);

  // ── Escape closes ────────────────────────────────────────────────────────
  // Same pattern as the other moveable floating dialog,
  // components/conference/PersonaPickerModal.tsx: a document-level keydown
  // listener mounted for as long as the dialog is. Ctrl/Cmd+Shift+A was the
  // ONLY way out before this, so a user who didn't know the shortcut was
  // stuck. (fix: GitHub #1141 defect 3)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const { events, byFeed, seenFeeds, connectionState, clear } = useAgentDebugStream(callId, { enabled: true });

  // Available feeds = those actually seen on the wire (resolved through the catalog).
  const availableFeeds = useMemo(
    () =>
      Array.from(seenFeeds)
        .map((key) => {
          const [source, feed] = key.split('/');
          return resolveDescriptor(source, feed);
        })
        .sort((a, b) => a.label.localeCompare(b.label)),
    [seenFeeds],
  );

  const openFeed = useCallback(
    (id: string) => {
      setLayout((l) => (l.openFeedIds.includes(id) ? l : { ...l, openFeedIds: [...l.openFeedIds, id] }));
      setActiveTab(id);
    },
    [],
  );
  const closeFeedTab = useCallback(
    (id: string) => {
      setLayout((l) => ({ ...l, openFeedIds: l.openFeedIds.filter((x) => x !== id) }));
      setActiveTab((t) => (t === id ? 'feeds' : t));
    },
    [],
  );

  const exportJsonl = useCallback(() => {
    const body = events.map((e) => JSON.stringify(e)).join('\n');
    const blob = new Blob([body], { type: 'application/x-ndjson' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `agent-debug-${callId ?? 'call'}-${Date.now()}.jsonl`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, [events, callId]);

  // ── Drag (title bar) ─────────────────────────────────────────────────────
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number; el: HTMLElement } | null>(null);
  const onDragStart = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      // Don't start a drag when the pointer-down landed on an interactive
      // control inside the title bar (pause / minimize / close, etc.).
      if ((e.target as HTMLElement).closest('button, input, a, [role="button"]')) return;
      if (e.button !== 0) return;
      const el = e.currentTarget;
      el.setPointerCapture?.(e.pointerId);
      dragRef.current = { startX: e.clientX, startY: e.clientY, origX: layout.x, origY: layout.y, el };
    },
    [layout.x, layout.y],
  );
  const onDragMove = useCallback((e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const nx = Math.max(0, Math.min(window.innerWidth - 80, d.origX + (e.clientX - d.startX)));
    const ny = Math.max(0, Math.min(window.innerHeight - 32, d.origY + (e.clientY - d.startY)));
    setLayout((l) => ({ ...l, x: nx, y: ny }));
  }, []);
  const onDragEnd = useCallback((e: React.PointerEvent) => {
    const d = dragRef.current;
    if (d) {
      try {
        d.el.releasePointerCapture?.(e.pointerId);
      } catch {
        /* ignore */
      }
    }
    dragRef.current = null;
  }, []);

  // ── Resize (corner handle) ───────────────────────────────────────────────
  const resizeRef = useRef<{ startX: number; startY: number; origW: number; origH: number } | null>(null);
  const onResizeStart = useCallback(
    (e: React.PointerEvent) => {
      e.stopPropagation();
      (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
      resizeRef.current = { startX: e.clientX, startY: e.clientY, origW: layout.w, origH: layout.h };
    },
    [layout.w, layout.h],
  );
  const onResizeMove = useCallback((e: React.PointerEvent) => {
    const r = resizeRef.current;
    if (!r) return;
    const nw = Math.max(MIN_W, r.origW + (e.clientX - r.startX));
    const nh = Math.max(MIN_H, r.origH + (e.clientY - r.startY));
    setLayout((l) => ({ ...l, w: nw, h: nh }));
  }, []);
  const onResizeEnd = useCallback(() => {
    resizeRef.current = null;
  }, []);

  // ── Minimized pill ───────────────────────────────────────────────────────
  if (layout.minimized) {
    return (
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal={false}
        aria-labelledby={titleId}
        tabIndex={-1}
        className="fixed z-[1100] flex items-center gap-2 rounded-md border border-white/[0.15] bg-[#0a0a0a] px-3 py-1.5 text-xs text-slate-200 shadow-lg outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
        style={{ left: layout.x, top: layout.y }}
        onPointerDown={onDragStart}
        onPointerMove={onDragMove}
        onPointerUp={onDragEnd}
      >
        <Bug className="h-3.5 w-3.5 text-slate-400" />
        <span id={titleId} className="font-medium">
          Agent Tools &amp; Thoughts
        </span>
        <Circle className={`h-2 w-2 ${CONN_DOT[connectionState]}`} fill="currentColor" />
        <span className="text-slate-500">{events.length}</span>
        <button onClick={() => setLayout((l) => ({ ...l, minimized: false }))} className="text-slate-400 hover:text-slate-200" title="Restore" aria-label="Restore Agent Tools dialog">
          <Square className="h-3 w-3" />
        </button>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-200" title="Close" aria-label="Close">
          <X className="h-3 w-3" />
        </button>
      </div>
    );
  }

  const renderActiveTab = () => {
    if (activeTab === 'feeds') {
      return (
        <div className="h-full overflow-auto bg-[#0a0a0a] p-2">
          <ul className="space-y-1">
            {/* Always-present cross-feed view. */}
            <li>
              <button
                onClick={() => openFeed(TIMELINE_FEED_ID)}
                className="flex w-full items-center gap-2 rounded border border-white/[0.08] bg-[#0d0d0d] px-2 py-1.5 text-left text-[12px] text-slate-200 hover:border-white/[0.2] hover:bg-white/[0.04]"
              >
                <Clock className="h-4 w-4 flex-shrink-0 text-slate-400" />
                <span className="flex-1 truncate">Turn Timeline (all feeds, by turn)</span>
                <span className="text-[10px] text-slate-500">{events.length} ev</span>
              </button>
            </li>
            {availableFeeds.length === 0 ? (
              <li className="p-2 text-[11px] text-slate-600">
                No feeds emitting yet for this call. (Feeds appear here once an agent emits at least one event.)
              </li>
            ) : (
              availableFeeds.map((d) => {
                const list = byFeed.get(d.id) ?? [];
                const last = list.length ? list[list.length - 1].ts : 0;
                const Icon = d.icon;
                return (
                  <li key={d.id}>
                    <button
                      onClick={() => openFeed(d.id)}
                      className="flex w-full items-center gap-2 rounded border border-white/[0.08] bg-[#0d0d0d] px-2 py-1.5 text-left text-[12px] text-slate-200 hover:border-white/[0.2] hover:bg-white/[0.04]"
                    >
                      <Icon className="h-4 w-4 flex-shrink-0 text-slate-400" />
                      <span className="flex-1 truncate">{d.label}</span>
                      <span className="text-[10px] text-slate-500">{list.length} ev</span>
                      {last ? <span className="text-[10px] text-slate-600">{new Date(last).toLocaleTimeString()}</span> : null}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      );
    }
    if (activeTab === TIMELINE_FEED_ID) {
      return <TurnTimelineRenderer events={events} callId={callId} paused={pausedAll} />;
    }
    const d = (() => {
      const [source, feed] = activeTab.split('/');
      return resolveDescriptor(source, feed);
    })();
    const Renderer = d.Renderer;
    return <Renderer events={byFeed.get(d.id) ?? []} callId={callId} paused={pausedAll} />;
  };

  return (
    <div
      ref={dialogRef}
      role="dialog"
      // Deliberately NOT aria-modal="true": this is a non-modal, draggable
      // inspector — the page underneath stays fully operable and there is no
      // focus trap. Claiming modality would make AT hide the rest of the page.
      aria-modal={false}
      aria-labelledby={titleId}
      tabIndex={-1}
      className="fixed z-[1100] flex flex-col overflow-hidden rounded-lg border border-white/[0.15] bg-[#0a0a0a] text-slate-200 shadow-2xl outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
      style={{ left: layout.x, top: layout.y, width: layout.w, height: layout.h }}
    >
      {/* Title bar (drag handle) */}
      <div
        className="flex cursor-move select-none items-center gap-2 border-b border-white/[0.12] bg-[#111] px-2 py-1.5 text-xs"
        onPointerDown={onDragStart}
        onPointerMove={onDragMove}
        onPointerUp={onDragEnd}
      >
        <Bug className="h-3.5 w-3.5 text-slate-400" />
        <span id={titleId} className="font-medium">
          Agent Tools &amp; Thoughts
        </span>
        <Circle className={`h-2 w-2 ${CONN_DOT[connectionState]}`} fill="currentColor" />
        <span className="font-mono text-[10px] text-slate-500">{callId ?? '(no call)'}</span>
        <span className="ml-auto flex items-center gap-1.5">
          <button onClick={() => setPausedAll((p) => !p)} className="text-slate-400 hover:text-slate-200" title={pausedAll ? 'Resume all' : 'Pause all'} aria-label={pausedAll ? 'Resume all feeds' : 'Pause all feeds'}>
            {pausedAll ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
          </button>
          <button onClick={clear} className="text-slate-400 hover:text-slate-200" title="Clear buffers" aria-label="Clear buffers">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
          <button onClick={exportJsonl} className="text-slate-400 hover:text-slate-200" title="Export JSONL" aria-label="Export JSONL">
            <Download className="h-3.5 w-3.5" />
          </button>
          <button onClick={() => setLayout((l) => ({ ...l, minimized: true }))} className="text-slate-400 hover:text-slate-200" title="Minimize" aria-label="Minimize Agent Tools dialog">
            <Minus className="h-3.5 w-3.5" />
          </button>
          {/* The X is an SVG icon with no text, so without an accessible name
              this control was invisible to screen readers and to any
              `[aria-label*="close"]` query. (fix: GitHub #1141 defect 3) */}
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200" title="Close" aria-label="Close">
            <X className="h-3.5 w-3.5" />
          </button>
        </span>
      </div>

      {/* Tab strip */}
      <div className="flex select-none items-center gap-1 overflow-x-auto border-b border-white/[0.12] bg-[#0d0d0d] px-1 py-1 text-[11px]">
        <button aria-pressed={activeTab === 'feeds'}
          onClick={() => setActiveTab('feeds')}
          className={`rounded px-2 py-0.5 ${activeTab === 'feeds' ? 'bg-white/[0.1] text-white' : 'text-slate-400 hover:bg-white/[0.05]'}`}
        >
          Available Feeds {availableFeeds.length ? `(${availableFeeds.length})` : ''}
        </button>
        {layout.openFeedIds.map((id) => (
          <span
            key={id}
            className={`flex items-center gap-1 rounded px-2 py-0.5 ${activeTab === id ? 'bg-white/[0.1] text-white' : 'text-slate-400 hover:bg-white/[0.05]'}`}
          >
            <button onClick={() => setActiveTab(id)} className="truncate max-w-[180px]">
              {tabLabel(id)}
            </button>
            <button onClick={() => closeFeedTab(id)} className="text-slate-500 hover:text-slate-200" title="Close tab" aria-label={`Close ${tabLabel(id)} tab`}>
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>

      {/* Active tab content */}
      <div className="min-h-0 flex-1">{renderActiveTab()}</div>

      {/* Resize handle (bottom-right) */}
      <div
        onPointerDown={onResizeStart}
        onPointerMove={onResizeMove}
        onPointerUp={onResizeEnd}
        className="absolute bottom-0 right-0 h-4 w-4 cursor-nwse-resize"
        style={{
          background: 'linear-gradient(135deg, transparent 50%, rgba(255,255,255,0.25) 50%, rgba(255,255,255,0.25) 60%, transparent 60%, transparent 70%, rgba(255,255,255,0.25) 70%, rgba(255,255,255,0.25) 80%, transparent 80%)',
        }}
        title="Resize"
      />
    </div>
  );
}

export default AgentDebugDialog;
